// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {DataLoader, Icon, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useState} from '@supermove/hooks';
import {OrganizationModel, UserModel} from '@supermove/models';
import {MomentType} from '@supermove/utils/src/Datetime';

// App
import EmptyState from '@shared/design/components/EmptyState';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import SalespersonCalendarSelect from '@shared/modules/Project/components/SalespersonCalendarSelect';
import SalespersonCalendarView from '@shared/modules/Project/components/SalespersonCalendarView';

const LoadingIndicator = Styled.Loading`
`;

const Container = Styled.View`
  flex: 1;
`;

const getRelatedOrganizationsOfficeStaffWithActiveAccountGrantOptions = ({
  organization,
}: {
  organization: OrganizationModel;
}) => {
  const relatedOrganizationsOfficeStaffOptions = _.flatten(
    organization.allCalendarUsers
      .filter((calendarUser) => {
        return !!calendarUser.user.primaryCalendar;
      })
      .map((calendarUser) => {
        return {
          value: _.toNumber(calendarUser.user.id),
          label: calendarUser.user.fullName,
          description: `${calendarUser.user.email}`,
        };
      }),
  );

  const sortedUsers: {
    value: number | null;
    label: string;
    description: string;
  }[] = _.sortBy(relatedOrganizationsOfficeStaffOptions, ['label']);
  return sortedUsers;
};

const SalespersonCalendarContent = ({
  organization,
  jobDate,
  initialSelectedSalesPersonIds,
  calendarsPerPage,
  handleSetUpSalesCalendar,
}: {
  organization: OrganizationModel;
  jobDate?: MomentType;
  initialSelectedSalesPersonIds?: number[];
  calendarsPerPage?: number;
  handleSetUpSalesCalendar: () => void;
}) => {
  const allSalespersonIds = organization.allCalendarUsers.map((calendarUser) =>
    _.toNumber(calendarUser.user.id),
  );

  const userIdToCalendarIdMap = organization.allCalendarUsers.reduce(
    (acc, calendarUser) => {
      if (calendarUser.user.primaryCalendar) {
        acc[_.toNumber(calendarUser.user.id)] = _.toString(calendarUser.user.primaryCalendar.id);
      }
      return acc;
    },
    {} as Record<number, string>,
  );

  const calendarUserIdToUserMap = organization.allCalendarUsers.reduce(
    (acc, calendarUser) => {
      acc[_.toNumber(calendarUser.user.id)] = calendarUser.user;
      return acc;
    },
    {} as Record<number, UserModel>,
  );

  const defaultSelectedSalesPersonIds = Array.from(
    new Set(
      initialSelectedSalesPersonIds ||
        organization.allCalendarUsers.map((calendarUser) => _.toNumber(calendarUser.user.id)),
    ),
  );

  const [selectedSalesPersonIds, setSelectedSalesPersonIds] = useState<number[]>(
    defaultSelectedSalesPersonIds,
  );

  const selectedUserWithCalendarId = (
    selectedSalesPersonIds.length ? selectedSalesPersonIds : allSalespersonIds
  )
    .filter((userId) => !!calendarUserIdToUserMap[userId])
    .map((userId) => ({
      user: calendarUserIdToUserMap[userId],
      calendarId: userIdToCalendarIdMap[userId],
    }));

  const salespersonOptions = getRelatedOrganizationsOfficeStaffWithActiveAccountGrantOptions({
    organization,
  });

  return (
    <Container>
      <SalespersonCalendarSelect
        selectedSalesPersonIds={selectedSalesPersonIds}
        salespersonOptions={salespersonOptions}
        handleSalesPersonIdsChange={setSelectedSalesPersonIds}
      />
      <Space height={8} />
      {salespersonOptions.length === 0 ? (
        <Container style={{justifyContent: 'center'}}>
          <EmptyState
            title={'No Sales Calendar.'}
            message={'Set up sales calendar(s) to view when scheduling survey jobs.'}
            primaryActionIconRight={Icon.AngleRight}
            primaryActionText={'Set up Sales Calendar'}
            handlePrimaryAction={handleSetUpSalesCalendar}
          />
        </Container>
      ) : selectedUserWithCalendarId.length === 0 ? (
        <Container style={{justifyContent: 'center'}}>
          <EmptyState
            title={'No Calendar Selected.'}
            message={'Select at least one sales calendar.'}
          />
        </Container>
      ) : (
        <SalespersonCalendarView
          selectedUserWithCalendarId={selectedUserWithCalendarId}
          jobDate={jobDate}
          calendarsPerPage={calendarsPerPage}
        />
      )}
    </Container>
  );
};

const SalespersonCalendar = ({
  initialSelectedSalesPersonIds,
  organizationSlug,
  jobDate,
  calendarsPerPage,
  handleSetUpSalesCalendar,
}: {
  initialSelectedSalesPersonIds?: number[];
  organizationSlug: string;
  jobDate?: MomentType;
  calendarsPerPage?: number;
  handleSetUpSalesCalendar: () => void;
}) => {
  const {data, loading} = useQuery<{organization: OrganizationModel}>(SalespersonCalendar.query, {
    variables: {slug: organizationSlug},
  });

  return (
    <ErrorCatcher>
      <DataLoader data={data} loading={loading} as={LoadingIndicator}>
        {({loadedData}) => (
          <SalespersonCalendarContent
            organization={loadedData.organization}
            jobDate={jobDate}
            initialSelectedSalesPersonIds={initialSelectedSalesPersonIds}
            calendarsPerPage={calendarsPerPage}
            handleSetUpSalesCalendar={handleSetUpSalesCalendar}
          />
        )}
      </DataLoader>
    </ErrorCatcher>
  );
};

SalespersonCalendar.query = gql`
  query SalespersonCalendar($slug: String!) {
    organization(slug: $slug) {
      id
      allCalendarUsers {
        id
        user {
          id
          email
          fullName
          primaryCalendar {
            id
          }
        }
      }
    }
  }
`;

export default SalespersonCalendar;
